<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import unhappyCasesAPI from 'dashboard/api/unhappyCases';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  STATUS_CLASSES,
  SIGNAL_CLASSES,
  useUnhappyCaseConstants,
} from 'dashboard/composables/useUnhappyCaseConstants';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const getters = useStoreGetters();
const { accountId } = useAccount();
const { STATUS_LABELS, SIGNAL_LABELS } = useUnhappyCaseConstants();

const currentUser = computed(() => getters.getCurrentUser.value);
const { isAdmin } = useAdmin();
const isNoteAuthor = note => note.author?.id === currentUser.value?.id;

const unhappyCase = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const noteContent = ref('');
const isNoteInputFocused = ref(false);
const isSavingNote = ref(false);
const editingNoteId = ref(null);
const editingNoteContent = ref('');
const isSavingEdit = ref(false);

const confirmDialog = ref(null);
const pendingDeleteFn = ref(null);

const openDeleteConfirm = fn => {
  pendingDeleteFn.value = fn;
  confirmDialog.value?.open();
};

const executePendingDelete = () => {
  pendingDeleteFn.value?.();
  pendingDeleteFn.value = null;
  confirmDialog.value?.close();
};

const reviewForm = ref({
  review_status: 'pending',
  reviewer_notes: '',
  confirmed_items: [],
});

const fetchCase = async () => {
  isLoading.value = true;
  try {
    const { data } = await unhappyCasesAPI.show(route.params.id);
    unhappyCase.value = data;
    reviewForm.value = {
      review_status: data.review_status,
      reviewer_notes: data.reviewer_notes || '',
      confirmed_items: (data.confirmed_items?.length
        ? JSON.parse(JSON.stringify(data.confirmed_items))
        : JSON.parse(JSON.stringify(data.ai_items || []))
      ).map(({ ai_quality: _q, ...item }) => item),
    };
  } finally {
    isLoading.value = false;
  }
};

const addItem = () => {
  reviewForm.value.confirmed_items.push({ reason: '', solution: '' });
};

const removeItem = idx => {
  reviewForm.value.confirmed_items.splice(idx, 1);
};

const saveReview = async status => {
  isSaving.value = true;
  try {
    const { data } = await unhappyCasesAPI.update(route.params.id, {
      ...reviewForm.value,
      review_status: status || reviewForm.value.review_status,
    });
    unhappyCase.value = data;
    reviewForm.value.review_status = data.review_status;
    useAlert(t('UNHAPPY_CASES.DETAIL.SAVE_SUCCESS'));
  } catch {
    useAlert(t('UNHAPPY_CASES.DETAIL.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const reject = () => saveReview('rejected');
const confirm = () => saveReview('confirmed');

const updateAiItemQuality = async (idx, n) => {
  const item = unhappyCase.value.ai_items?.[idx];
  if (!item) return;
  item.ai_quality = item.ai_quality === n ? null : n;
  try {
    const { data } = await unhappyCasesAPI.update(route.params.id, {
      ai_items: unhappyCase.value.ai_items,
    });
    unhappyCase.value = data;
  } catch {
    useAlert(t('UNHAPPY_CASES.DETAIL.SAVE_ERROR'));
  }
};

const approveTrusted = async () => {
  isSaving.value = true;
  try {
    const { data } = await unhappyCasesAPI.trust(route.params.id);
    unhappyCase.value = data;
    useAlert(t('UNHAPPY_CASES.DETAIL.TRUST_SUCCESS'));
  } catch {
    useAlert(t('UNHAPPY_CASES.DETAIL.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const removeTrusted = async () => {
  isSaving.value = true;
  try {
    const { data } = await unhappyCasesAPI.untrust(route.params.id);
    unhappyCase.value = data;
    useAlert(t('UNHAPPY_CASES.DETAIL.UNTRUST_SUCCESS'));
  } catch {
    useAlert(t('UNHAPPY_CASES.DETAIL.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const startEditNote = note => {
  editingNoteId.value = note.id;
  editingNoteContent.value = note.content;
};

const cancelEditNote = () => {
  editingNoteId.value = null;
  editingNoteContent.value = '';
};

const saveEditNote = async noteId => {
  if (!editingNoteContent.value.trim()) return;
  isSavingEdit.value = true;
  try {
    const { data } = await unhappyCasesAPI.updateNote(
      route.params.id,
      noteId,
      editingNoteContent.value
    );
    const idx = unhappyCase.value.notes.findIndex(n => n.id === noteId);
    if (idx !== -1) unhappyCase.value.notes[idx] = data;
    cancelEditNote();
  } catch {
    useAlert(t('UNHAPPY_CASES.DETAIL.NOTE_ERROR'));
  } finally {
    isSavingEdit.value = false;
  }
};

const removeNote = async noteId => {
  try {
    await unhappyCasesAPI.deleteNote(route.params.id, noteId);
    unhappyCase.value.notes = unhappyCase.value.notes.filter(
      n => n.id !== noteId
    );
  } catch {
    useAlert(t('UNHAPPY_CASES.DETAIL.NOTE_ERROR'));
  }
};

const addNote = async () => {
  if (!noteContent.value.trim()) return;
  isSavingNote.value = true;
  try {
    const { data } = await unhappyCasesAPI.createNote(
      route.params.id,
      noteContent.value
    );
    unhappyCase.value.notes = [...(unhappyCase.value.notes || []), data];
    noteContent.value = '';
    useAlert(t('UNHAPPY_CASES.DETAIL.NOTE_SUCCESS'));
  } catch {
    useAlert(t('UNHAPPY_CASES.DETAIL.NOTE_ERROR'));
  } finally {
    isSavingNote.value = false;
  }
};

const formatDatetime = ts => {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleString();
};

const goBack = () => {
  router.push({
    name: 'unhappy_cases_index',
    params: { accountId: accountId.value },
  });
};

const conversationUrl = computed(() => {
  if (!unhappyCase.value?.conversation?.display_id) return null;
  return `/app/accounts/${accountId.value}/conversations/${unhappyCase.value.conversation.display_id}`;
});

onBeforeMount(fetchCase);
</script>

<template>
  <div
    class="flex flex-col w-full h-full overflow-hidden bg-n-background font-inter"
  >
    <!-- Top bar -->
    <div
      class="shrink-0 flex items-center gap-3 px-5 py-3 border-b border-n-weak bg-n-solid-1"
    >
      <button
        type="button"
        class="flex items-center gap-1.5 text-sm text-n-slate-10 hover:text-n-slate-12 transition-colors"
        @click="goBack"
      >
        <fluent-icon icon="chevron-left" size="14" />
        {{ $t('UNHAPPY_CASES.DETAIL.BACK') }}
      </button>

      <template v-if="unhappyCase">
        <div class="w-px h-4 bg-n-weak" />
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span class="text-sm font-semibold text-n-slate-12">
          #{{
            unhappyCase.conversation?.display_id || unhappyCase.conversation_id
          }}
        </span>
        <span
          v-if="unhappyCase.conversation?.contact_name"
          class="text-sm text-n-slate-9"
        >
          {{ unhappyCase.conversation.contact_name }}
        </span>
        <span
          class="px-2 py-0.5 rounded-full text-xs font-medium border"
          :class="STATUS_CLASSES[unhappyCase.review_status]"
        >
          {{ STATUS_LABELS[unhappyCase.review_status] }}
        </span>
        <span
          v-if="unhappyCase.is_trusted"
          class="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-n-iris-5 bg-n-iris-3 text-n-iris-11"
        >
          <fluent-icon icon="star-emphasis" size="11" />
          {{ $t('UNHAPPY_CASES.DETAIL.TRUSTED') }}
        </span>

        <a
          v-if="conversationUrl"
          :href="conversationUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="ml-auto flex items-center gap-1 text-sm text-n-slate-9 hover:text-n-slate-12 transition-colors"
        >
          {{ $t('UNHAPPY_CASES.DETAIL.CONVERSATION_LINK') }}
          <fluent-icon icon="arrow-up-right" size="13" />
        </a>
      </template>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex-1 flex items-center justify-center text-sm text-n-slate-9"
    >
      {{ $t('UNHAPPY_CASES.LIST.LOADING') }}
    </div>

    <!-- Two-column body -->
    <div v-else-if="unhappyCase" class="flex flex-1 overflow-hidden">
      <!-- Left: read-only information -->
      <div class="flex-1 overflow-y-auto p-6 flex flex-col gap-6 min-w-0">
        <!-- Signals — inline label + tags -->
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="shrink-0 text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            {{ $t('UNHAPPY_CASES.DETAIL.SECTIONS.SIGNALS') }}
          </span>
          <span
            v-for="signal in unhappyCase.signals"
            :key="signal.type"
            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
            :class="
              SIGNAL_CLASSES[signal.type] ||
              'bg-n-alpha-2 text-n-slate-11 border-n-weak'
            "
          >
            {{ SIGNAL_LABELS[signal.type] || signal.type }}
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <span v-if="signal.value" class="opacity-70 font-normal">
              · {{ signal.value }}
            </span>
          </span>
        </div>

        <!-- AI Analysis -->
        <section class="flex flex-col gap-3">
          <h3
            class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            {{ $t('UNHAPPY_CASES.DETAIL.SECTIONS.AI_ANALYSIS') }}
          </h3>
          <div
            class="rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden"
          >
            <div v-if="unhappyCase.ai_items?.length">
              <!-- Meta row -->
              <div
                class="flex items-center gap-2 px-4 py-2.5 border-b border-n-weak bg-n-alpha-1 text-xs text-n-slate-9"
              >
                <span>{{ unhappyCase.ai_model }}</span>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <span class="text-n-slate-6">·</span>
                <span>{{ formatDatetime(unhappyCase.ai_extracted_at) }}</span>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <span class="text-n-slate-6">·</span>
                <span>
                  {{ $t('UNHAPPY_CASES.DETAIL.AI.RESOLVE') }}
                  {{ unhappyCase.resolve_count }}
                </span>
              </div>
              <!-- Items -->
              <div
                v-for="(item, idx) in unhappyCase.ai_items"
                :key="idx"
                class="px-4 py-3 border-b border-n-weak last:border-0"
              >
                <div class="flex items-center justify-between mb-1.5">
                  <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                  <span class="text-xs text-n-slate-6 font-medium tabular-nums">
                    #{{ idx + 1 }}
                  </span>
                  <!-- Per-item Good / Bad rating -->
                  <div class="flex items-center gap-1">
                    <button
                      type="button"
                      class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-medium transition-colors"
                      :class="
                        item.ai_quality === 'good'
                          ? 'bg-n-teal-3 text-n-teal-11 border-n-teal-5'
                          : 'bg-n-alpha-1 text-n-slate-7 border-n-weak hover:border-n-teal-5 hover:text-n-teal-9'
                      "
                      @click="updateAiItemQuality(idx, 'good')"
                    >
                      <span class="i-lucide-thumbs-up size-3 shrink-0" />
                      {{ $t('UNHAPPY_CASES.DETAIL.REVIEW.GOOD') }}
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-medium transition-colors"
                      :class="
                        item.ai_quality === 'bad'
                          ? 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-5'
                          : 'bg-n-alpha-1 text-n-slate-7 border-n-weak hover:border-n-ruby-5 hover:text-n-ruby-9'
                      "
                      @click="updateAiItemQuality(idx, 'bad')"
                    >
                      <span class="i-lucide-thumbs-down size-3 shrink-0" />
                      {{ $t('UNHAPPY_CASES.DETAIL.REVIEW.BAD') }}
                    </button>
                  </div>
                </div>
                <div class="flex flex-col gap-1 text-sm">
                  <div>
                    <span class="font-medium text-n-slate-10 mr-1.5">
                      {{ $t('UNHAPPY_CASES.DETAIL.AI.REASON') }}
                    </span>
                    <span class="text-n-slate-12">{{ item.reason }}</span>
                  </div>
                  <div>
                    <span class="font-medium text-n-slate-10 mr-1.5">
                      {{ $t('UNHAPPY_CASES.DETAIL.AI.SOLUTION') }}
                    </span>
                    <span class="text-n-slate-12">{{ item.solution }}</span>
                  </div>
                </div>
              </div>
            </div>
            <p v-else class="px-4 py-6 text-sm text-n-slate-9 text-center">
              {{ $t('UNHAPPY_CASES.DETAIL.AI.NO_ITEMS') }}
            </p>
          </div>

          <!-- Past extractions -->
          <details
            v-if="unhappyCase.ai_extraction_history?.length > 1"
            class="group"
          >
            <summary
              class="list-none flex items-center gap-1.5 text-sm text-n-slate-9 hover:text-n-slate-12 cursor-pointer select-none transition-colors"
            >
              <fluent-icon
                icon="chevron-right"
                size="12"
                class="transition-transform group-open:rotate-90"
              />
              {{ $t('UNHAPPY_CASES.DETAIL.SECTIONS.HISTORY') }}
            </summary>
            <div class="mt-2 flex flex-col gap-2 pl-4">
              <div
                v-for="cycle in unhappyCase.ai_extraction_history.slice(0, -1)"
                :key="cycle.resolve_count"
                class="p-3 border border-n-weak rounded-lg text-sm bg-n-solid-2"
              >
                <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
                <div class="text-xs text-n-slate-9 mb-2">
                  {{ $t('UNHAPPY_CASES.DETAIL.AI.RESOLVE') }}
                  {{ cycle.resolve_count }}
                  · {{ cycle.model }}
                </div>
                <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
                <div
                  v-for="(aiItem, aiIdx) in cycle.items"
                  :key="aiIdx"
                  class="text-n-slate-11 mb-1 last:mb-0"
                >
                  <span class="font-medium">{{ aiItem.reason }}</span>
                  <span class="text-n-slate-6 mx-1">{{
                    $t('UNHAPPY_CASES.SYMBOLS.ARROW_RIGHT')
                  }}</span>
                  <span>{{ aiItem.solution }}</span>
                </div>
              </div>
            </div>
          </details>
        </section>

        <!-- Notes -->
        <section class="flex flex-col gap-3">
          <h3
            class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            {{ $t('UNHAPPY_CASES.DETAIL.SECTIONS.NOTES') }}
          </h3>

          <!-- Chat feed -->
          <div class="flex flex-col gap-4">
            <p
              v-if="!unhappyCase.notes?.length"
              class="text-sm text-n-slate-7 text-center py-2"
            >
              {{ $t('UNHAPPY_CASES.DETAIL.NOTES.EMPTY') }}
            </p>
            <div
              v-for="note in unhappyCase.notes"
              :key="note.id"
              class="group flex items-start gap-2.5"
            >
              <!-- Avatar -->
              <div
                class="shrink-0 w-7 h-7 rounded-full bg-n-slate-3 flex items-center justify-center text-xs font-semibold text-n-slate-11 mt-0.5"
              >
                {{ (note.author?.name || '?')[0].toUpperCase() }}
              </div>

              <!-- Bubble + meta -->
              <div class="flex flex-col gap-1 min-w-0 flex-1">
                <div class="flex items-center justify-between gap-2">
                  <div class="flex items-center gap-1.5 text-xs text-n-slate-9">
                    <span class="font-medium text-n-slate-11">{{
                      note.author?.name
                    }}</span>
                    <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                    <span class="text-n-slate-5">·</span>
                    <span>{{ formatDatetime(note.created_at) }}</span>
                  </div>
                  <!-- Hover actions -->
                  <div
                    v-if="editingNoteId !== note.id"
                    class="flex items-center gap-2.5 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                  >
                    <button
                      v-if="isNoteAuthor(note)"
                      type="button"
                      class="text-n-slate-8 hover:text-n-slate-12 transition-colors"
                      @click="startEditNote(note)"
                    >
                      {{ $t('UNHAPPY_CASES.DETAIL.NOTES.EDIT') }}
                    </button>
                    <button
                      v-if="isNoteAuthor(note) || isAdmin"
                      type="button"
                      class="text-n-ruby-9 hover:text-n-ruby-11 transition-colors"
                      @click="openDeleteConfirm(() => removeNote(note.id))"
                    >
                      {{ $t('UNHAPPY_CASES.DETAIL.NOTES.DELETE') }}
                    </button>
                  </div>
                </div>

                <!-- View bubble -->
                <div
                  v-if="editingNoteId !== note.id"
                  class="w-fit max-w-[80%] bg-n-alpha-2 border border-n-weak rounded-2xl rounded-tl-none px-3 py-2"
                >
                  <p
                    class="text-sm text-n-slate-12 leading-relaxed whitespace-pre-wrap"
                  >
                    {{ note.content }}
                  </p>
                </div>

                <!-- Edit mode -->
                <div v-else class="flex flex-col gap-2">
                  <textarea
                    v-model="editingNoteContent"
                    rows="2"
                    class="w-full px-3 py-2 text-sm border border-n-weak rounded-xl bg-n-background text-n-slate-12 resize-none focus:outline-none focus:border-n-slate-7 transition-colors"
                  />
                  <div class="flex gap-2">
                    <button
                      type="button"
                      class="px-3 py-1 text-xs font-medium rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-40 transition-colors"
                      :disabled="isSavingEdit || !editingNoteContent.trim()"
                      @click="saveEditNote(note.id)"
                    >
                      {{
                        isSavingEdit
                          ? $t('UNHAPPY_CASES.DETAIL.NOTES.SAVING')
                          : $t('UNHAPPY_CASES.DETAIL.NOTES.SAVE')
                      }}
                    </button>
                    <button
                      type="button"
                      class="px-3 py-1 text-xs font-medium rounded-lg border border-n-weak text-n-slate-9 hover:text-n-slate-12 transition-colors"
                      @click="cancelEditNote"
                    >
                      {{ $t('UNHAPPY_CASES.DETAIL.NOTES.CANCEL') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Chat input -->
          <div class="pt-2 border-t border-n-weak">
            <div
              class="border rounded-2xl bg-n-solid-2 transition-colors overflow-hidden hover:border-n-slate-7"
              :class="
                isNoteInputFocused
                  ? 'border-n-slate-7 ring-1 ring-n-slate-7/30'
                  : 'border-n-weak'
              "
            >
              <textarea
                v-model="noteContent"
                rows="2"
                class="w-full px-3 pt-2.5 pb-1 text-sm bg-transparent text-n-slate-12 resize-none placeholder:text-n-slate-7 outline-0 border-0"
                :placeholder="$t('UNHAPPY_CASES.DETAIL.NOTES.PLACEHOLDER')"
                @focus="isNoteInputFocused = true"
                @blur="isNoteInputFocused = false"
                @keydown.ctrl.enter="addNote"
                @keydown.meta.enter="addNote"
              />
              <div class="flex justify-end px-2 pb-2">
                <button
                  type="button"
                  class="px-3 py-1 text-xs font-medium rounded-xl bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-40 transition-colors"
                  :disabled="isSavingNote || !noteContent.trim()"
                  @click="addNote"
                >
                  {{
                    isSavingNote
                      ? $t('UNHAPPY_CASES.DETAIL.NOTES.SAVING')
                      : $t('UNHAPPY_CASES.DETAIL.NOTES.SAVE')
                  }}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- Right: review panel -->
      <div
        class="w-80 xl:w-96 shrink-0 flex flex-col border-l border-n-weak overflow-hidden"
      >
        <div class="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          <!-- Review status -->
          <div class="flex flex-col gap-2">
            <label
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              {{ $t('UNHAPPY_CASES.DETAIL.REVIEW.STATUS_LABEL') }}
            </label>
            <div
              class="flex gap-1 p-1 rounded-xl bg-n-alpha-1 border border-n-weak"
            >
              <button
                v-for="opt in ['pending', 'confirmed', 'rejected']"
                :key="opt"
                type="button"
                class="flex-1 py-1.5 text-xs rounded-lg transition-colors font-medium"
                :class="
                  reviewForm.review_status === opt
                    ? STATUS_CLASSES[opt] + ' shadow-sm'
                    : 'text-n-slate-9 hover:text-n-slate-12'
                "
                @click="reviewForm.review_status = opt"
              >
                {{ STATUS_LABELS[opt] }}
              </button>
            </div>
          </div>

          <!-- Reviewer / Trusted meta -->
          <div
            v-if="unhappyCase.reviewed_by || unhappyCase.trusted_by"
            class="flex flex-col gap-1.5 px-3 py-2.5 rounded-lg bg-n-alpha-1 border border-n-weak text-xs text-n-slate-9"
          >
            <div
              v-if="unhappyCase.reviewed_by"
              class="flex items-center justify-between gap-2"
            >
              <div class="flex items-center gap-1.5">
                <fluent-icon icon="person" size="12" class="shrink-0" />
                <span>{{ unhappyCase.reviewed_by.name }}</span>
              </div>
              <span>{{ formatDatetime(unhappyCase.reviewed_at) }}</span>
            </div>
            <div
              v-if="unhappyCase.trusted_by"
              class="flex items-center justify-between gap-2 text-n-iris-9"
            >
              <div class="flex items-center gap-1.5">
                <fluent-icon icon="star-emphasis" size="12" class="shrink-0" />
                <span>{{ unhappyCase.trusted_by.name }}</span>
              </div>
              <span>{{ formatDatetime(unhappyCase.trusted_at) }}</span>
            </div>
          </div>

          <!-- Confirmed items -->
          <div class="flex flex-col gap-2">
            <label
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              {{ $t('UNHAPPY_CASES.DETAIL.REVIEW.CONFIRMED_ITEMS') }}
            </label>
            <div
              v-for="(confItem, confIdx) in reviewForm.confirmed_items"
              :key="confIdx"
              class="flex flex-col gap-2 p-3 border border-n-weak rounded-lg bg-n-solid-2"
            >
              <div class="flex items-center justify-between">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <span class="text-xs text-n-slate-6 font-medium tabular-nums">
                  #{{ confIdx + 1 }}
                </span>
                <button
                  type="button"
                  class="text-xs text-n-ruby-9 hover:text-n-ruby-11 transition-colors"
                  @click="openDeleteConfirm(() => removeItem(confIdx))"
                >
                  {{ $t('UNHAPPY_CASES.DETAIL.REVIEW.REMOVE') }}
                </button>
              </div>
              <textarea
                v-model="confItem.reason"
                rows="2"
                class="w-full px-2.5 py-1.5 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 resize-none placeholder:text-n-slate-7 focus:outline-none focus:border-n-slate-7 transition-colors"
                :placeholder="$t('UNHAPPY_CASES.DETAIL.AI.REASON')"
              />
              <textarea
                v-model="confItem.solution"
                rows="2"
                class="w-full px-2.5 py-1.5 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 resize-none placeholder:text-n-slate-7 focus:outline-none focus:border-n-slate-7 transition-colors"
                :placeholder="$t('UNHAPPY_CASES.DETAIL.AI.SOLUTION')"
              />
            </div>
            <button
              type="button"
              class="text-sm text-n-slate-9 hover:text-n-slate-12 transition-colors text-left"
              @click="addItem"
            >
              {{ $t('UNHAPPY_CASES.DETAIL.REVIEW.ADD_ITEM') }}
            </button>
          </div>

          <!-- Reviewer notes -->
          <div class="flex flex-col gap-2">
            <label
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              {{ $t('UNHAPPY_CASES.DETAIL.REVIEW.NOTES_LABEL') }}
            </label>
            <textarea
              v-model="reviewForm.reviewer_notes"
              rows="3"
              class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-solid-2 text-n-slate-12 resize-none placeholder:text-n-slate-7 focus:outline-none focus:border-n-slate-7 transition-colors"
              :placeholder="$t('UNHAPPY_CASES.DETAIL.REVIEW.NOTES_PLACEHOLDER')"
            />
          </div>
        </div>

        <!-- Sticky action footer -->
        <div
          class="shrink-0 border-t border-n-weak px-5 py-4 flex flex-col gap-2 bg-n-solid-1"
        >
          <div class="flex gap-2">
            <button
              type="button"
              class="flex-1 py-2 text-sm font-medium rounded-lg border transition-colors border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11 hover:bg-n-ruby-4 disabled:opacity-40"
              :disabled="isSaving"
              @click="reject"
            >
              {{ $t('UNHAPPY_CASES.DETAIL.ACTIONS.REJECT') }}
            </button>
            <button
              type="button"
              class="flex-1 py-2 text-sm font-medium rounded-lg transition-colors bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-40"
              :disabled="isSaving"
              @click="confirm"
            >
              {{
                isSaving
                  ? $t('UNHAPPY_CASES.DETAIL.ACTIONS.SAVING')
                  : $t('UNHAPPY_CASES.DETAIL.ACTIONS.SAVE_CONFIRM')
              }}
            </button>
          </div>
          <template v-if="isAdmin">
            <button
              v-if="
                !unhappyCase.is_trusted &&
                unhappyCase.review_status === 'confirmed'
              "
              type="button"
              class="w-full py-2 text-sm font-medium rounded-lg border transition-colors border-n-iris-5 bg-n-iris-3 text-n-iris-11 hover:bg-n-iris-4 disabled:opacity-40"
              :disabled="isSaving"
              @click="approveTrusted"
            >
              <fluent-icon icon="star-emphasis" size="13" class="inline mr-1" />
              {{ $t('UNHAPPY_CASES.DETAIL.ACTIONS.TRUST') }}
            </button>
            <button
              v-else-if="unhappyCase.is_trusted"
              type="button"
              class="w-full py-2 text-sm font-medium rounded-lg border transition-colors border-n-slate-5 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:opacity-40"
              :disabled="isSaving"
              @click="removeTrusted"
            >
              <fluent-icon
                icon="star-emphasis"
                size="13"
                class="inline mr-1 opacity-40"
              />
              {{ $t('UNHAPPY_CASES.DETAIL.ACTIONS.UNTRUST') }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>

  <Dialog
    ref="confirmDialog"
    type="alert"
    :title="$t('UNHAPPY_CASES.DETAIL.DELETE_CONFIRM.TITLE')"
    :description="$t('UNHAPPY_CASES.DETAIL.DELETE_CONFIRM.DESCRIPTION')"
    :confirm-button-label="$t('UNHAPPY_CASES.DETAIL.DELETE_CONFIRM.CONFIRM')"
    @confirm="executePendingDelete"
  />
</template>
